import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { IMAGES } from "../constants/theme";
import { useAuth } from "../auth/AuthContext";
import "./AuthPage.css";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(
    searchParams.get("mode") === "signup" ? "signup" : "login",
  );
  const navigate = useNavigate();

  const { login, register } = useAuth();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
    country: "",
    city: "",
    street: "",
    building: "",
    trainings_per_week: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setLoading(true);

    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate(searchParams.get("redirect") === "program-inquiry" ? "/my-program" : "/home-2");
      } else {
        await register({
          name: form.name,
          email: form.email,
          phone: form.phone,
          password: form.password,
          password_confirmation: form.password_confirmation,
          country: form.country,
          city: form.city,
          street: form.street,
          building: form.building,
          trainings_per_week: form.trainings_per_week
        });

        navigate(searchParams.get("redirect") === "program-inquiry" ? "/my-program" : "/home-2");
      }
    } catch (err) {
      setError(err.response?.data?.message || t("Authentication failed"));
    }

    setLoading(false);
  };

  return (
    <div className="page-wraper auth-page-wrap">
      <LanguageSwitcher className="hpa-auth-language-switch" />
      <div
        className="page-content auth-page-content"
        style={{
          backgroundImage: `url(${IMAGES.AuthPerformanceBg})`
        }}
      >
        <section className="appointment-page auth-hero">
          <div
            className={`container auth-shell ${
              mode === "login" ? "auth-shell-login" : "auth-shell-signup"
            }`}
          >
            <div
              className={`auth-card ${
                mode === "signup" ? "auth-card-signup" : ""
              }`}
            >
              <div className="auth-logo-wrap">
                <img src={IMAGES.logo} alt="Hybrid Performance Academy" className="auth-logo hpa-auth-logo" />
              </div>

              <div className="section-head text-center mb-4">
                <h3 className="title mb-0">
                  {mode === "login"
                    ? t("Welcome Back")
                    : t("Create Your Account")}
                </h3>
              </div>

              {/* Mode Switch */}
              <div className="auth-mode-switch">
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className={`auth-mode-btn ${
                    mode === "login" ? "active" : ""
                  }`}
                >
                  {t("Login")}
                </button>

                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className={`auth-mode-btn ${
                    mode === "signup" ? "active" : ""
                  }`}
                >
                  {t("Sign Up")}
                </button>
              </div>

              {error && (
                <div className="auth-error">
                  {error}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className={`auth-form ${mode === "signup" ? "auth-form-signup" : ""}`}
              >
                {mode === "signup" && (
                  <div className="auth-signup-grid">
                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="name"
                        className="form-control"
                        placeholder={t("Full Name")}
                        required
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="phone"
                        className="form-control"
                        placeholder={t("Phone Number")}
                        required
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="country"
                        className="form-control"
                        placeholder={t("Country")}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="city"
                        className="form-control"
                        placeholder={t("City")}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="street"
                        className="form-control"
                        placeholder={t("Street")}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <input
                        type="text"
                        name="building"
                        className="form-control"
                        placeholder={t("Building")}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group mb-3">
                      <select
                        name="trainings_per_week"
                        className="form-control"
                        onChange={handleChange}
                      >
                        <option value="">
                          {t("Training Sessions Per Week")}
                        </option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-group mb-3">
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    placeholder={t("Email Address")}
                    required
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group mb-3">
                  <input
                    type="password"
                    name="password"
                    className="form-control"
                    placeholder={t("Password")}
                    required
                    onChange={handleChange}
                  />
                </div>

                {mode === "signup" && (
                  <div className="form-group mb-3">
                    <input
                      type="password"
                      name="password_confirmation"
                      className="form-control"
                      placeholder={t("Confirm Password")}
                      required
                      onChange={handleChange}
                    />
                  </div>
                )}

                {mode === "login" && (
                  <div className="auth-forgot">
                    <Link to="/forgot-password">
                      {t("Forgot password?")}
                    </Link>
                  </div>
                )}

                <button
                  type="submit"
                  className={`btn btn-primary btn-lg btn-skew w-100 auth-submit ${
                    loading ? "auth-submit-loading" : ""
                  }`}
                  disabled={loading}
                >
                  <span>
                    {loading
                      ? mode === "login"
                        ? t("Logging in")
                        : t("Creating account")
                      : mode === "login"
                      ? t("Login")
                      : t("Create Account")}
                  </span>
                </button>
              </form>

              <p className="text-center mt-4 mb-0 auth-switch-text">
                {mode === "login" ? (
                  <>
                    {t("Don’t have an account?")}{" "}
                    <span
                      className="auth-switch-link"
                      onClick={() => setMode("signup")}
                    >
                      {t("Sign up")}
                    </span>
                  </>
                ) : (
                  <>
                    {t("Already have an account?")}{" "}
                    <span
                      className="auth-switch-link"
                      onClick={() => setMode("login")}
                    >
                      {t("Login")}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AuthPage;
